import { plainToClass, plainToClassFromExist } from "class-transformer";
import { ResponsesModel } from "@/models/classes/responses";
import { ResponseModel } from "@/models/classes/response";
import {
  CalculateOutRange,
  CheckActivityBeforeCheckout,
  ClosedReasonItemModel,
  CustomerPlanVisitModel,
  EmployeePreVisitModel,
  OutRangeVisitItemModel,
  SuccessPreVisitModel,
  SuccessSurveyModel,
  SurveyItemModel,
  SurveyListModel,
} from "@/models/classes/plan/visit";
import { SuccessModel } from "@/models/classes/success";
import { VisitApi } from "@/services/api/plan/visit";
import APIFile from "@/services/api/file";

const api = new VisitApi();
const fileApi = new APIFile();

export class VisitService {
  fetchGetVisitCustomerOnPlan = async (params: any = {}) => {
    const resData = await api.getVisitCustomerOnPlan(params);
    // console.log("resData", resData);
    const resModel = plainToClassFromExist(
      new ResponseModel<CustomerPlanVisitModel>(CustomerPlanVisitModel),
      resData,
    );
    if (resModel.status == 200 && typeof resModel.data == "object") {
      return resModel.data;
    }
    return undefined;
  };

  fetchGetVisitCustomerOffPlan = async (params: any = {}) => {
    const resData = await api.getVisitCustomerOffPlan(params);
    // console.log("resData", resData);
    const resModel = plainToClassFromExist(
      new ResponseModel<CustomerPlanVisitModel>(CustomerPlanVisitModel),
      resData,
    );
    if (resModel.status == 200 && typeof resModel.data == "object") {
      return resModel.data;
    }
    return undefined;
  };

  fetchGetOutRangeVisitReason = async (params: any = {}) => {
    //params.channelId require
    // params['channelId'] = "TT"
    const resData = await api.getOutRangeVisitReason(params);
    const resModel = plainToClassFromExist(
      new ResponseModel<OutRangeVisitItemModel[]>(OutRangeVisitItemModel),
      resData,
    );
    if (resModel.status == 200 && typeof resModel.data == "object") {
      return await resModel.data;
    }
    return undefined;
  };

  fetchGetCalculateOutRange = async (params: {
    customerId: string | number;
    customerPlanId: string | number;
    lat: string | number;
    lng: string | number;
  }) => {
    const resData = await api.getCalculateOutRange(params);
    const resModel = plainToClassFromExist(
      new ResponseModel<CalculateOutRange>(CalculateOutRange),
      resData,
    );
    if (resModel.status == 200 && typeof resModel.data == "object") {
      return await resModel.data;
    }
    return undefined;
  };

  fetchPostCheckInEmployeeVisit = async (params: any = {}) => {
    let payload: any = {
      customerId: params.customerId,
      customerPlanId: params.customerPlanId,
      latitude: params.latitude,
      longitude: params.longitude,
      visitPic: null,
      checkInNoted: params.checkInNoted,
      //for offline
      planID: params?.planID,
      routePlanId: params?.routePlanId,
    };

    if (window.isOffline) {
      payload.visitPic = params.imageBase64;
    } else if (params.imageBase64) {
      let file = await fileApi.storageUpload64(
        "ka-checkin",
        params.imageBase64,
      );
      if (file.privateUrl && file.privateUrl != null && file.privateUrl != "") {
        payload.visitPic = "https://" + file.privateUrl;
      }
    } else {
      payload.visitPic = "";
    }

    const resData = await api.postCheckInEmployeeVisit(payload);
    const resModel = plainToClassFromExist(
      new ResponseModel<SuccessModel>(SuccessModel),
      resData,
    );

    if (resModel.status == 200 && typeof resModel.data == "object") {
      return resModel.data;
    }
    return undefined;
  };

  fetchPostCheckInEmployeeVisitOffPlan = async (params: any = {}) => {
    let payload: any = {
      customerId: params.customerId,
      latitude: params.latitude,
      longitude: params.longitude,
      visitPic: null,
      checkInNoted: params.checkInNoted,
    };

    if (window.isOffline) {
      payload.visitPic = params.imageBase64;
    } else if (params.imageBase64) {
      let file = await fileApi.storageUpload64(
        "ka-checkin",
        params.imageBase64,
      );
      if (file.privateUrl && file.privateUrl != null && file.privateUrl != "") {
        payload.visitPic = "https://" + file.privateUrl;
      }
    } else {
      payload.visitPic = "";
    }

    const resData = await api.postCheckInEmployeeVisitOffPlan(payload);
    const resModel = plainToClassFromExist(
      new ResponseModel<SuccessModel>(SuccessModel),
      resData,
    );
    if (resModel.status == 200 && typeof resModel.data == "object") {
      return resModel.data;
    }
    return undefined;
  };

  fetchPostCheckOutEmployeeVisit = async (params: any = {}) => {
    let payload: any = {
      customerId: params.customerId,
      customerPlanId: params.customerPlanId,
      latitude: params.latitude,
      longitude: params.longitude,
      note: params.note,
      CheckOutNote: params.CheckOutNote ?? "", // for outrange reason
      noActivityNote: params.noActivityNote ?? "", // for no-activity reason
    };
    const resData = await api.postCheckOutEmployeeVisit(payload);
    const resModel = plainToClassFromExist(
      new ResponseModel<SuccessModel>(SuccessModel),
      resData,
    );
    if (resModel.status == 200 && typeof resModel.data == "object") {
      return resModel.data;
    }
    return undefined;
  };

  fetchGetEmployeePreVisit = async (params: any = {}) => {
    const resData = await api.getEmployeePreVisit(params);
    const resModel = plainToClassFromExist(
      new ResponseModel<EmployeePreVisitModel>(EmployeePreVisitModel),
      resData,
    );
    if (resModel.status == 200 && typeof resModel.data == "object") {
      return resModel.data;
    }
    return undefined;
  };

  fetchAddEmployeePreVisit = async (params: any = {}) => {
    const resData = await api.postAddEmployeePreVisit(params);
    const resModel = plainToClassFromExist(
      new ResponseModel<SuccessPreVisitModel>(SuccessPreVisitModel),
      resData,
    );
    if (resModel.status == 200 && typeof resModel.data == "object") {
      return resModel.data;
    }
    return undefined;
  };

  fetchUpdateEmployeePreVisit = async (params: any = {}) => {
    const resData = await api.postUpdateEmployeePreVisit(params);
    const resModel = plainToClassFromExist(
      new ResponseModel<SuccessPreVisitModel>(SuccessPreVisitModel),
      resData,
    );
    if (resModel.status == 200 && typeof resModel.data == "object") {
      return resModel.data;
    }
    return undefined;
  };

  fetchGetListSurvey = async (params: any = {}) => {
    const resData = await api.getListSurvey(params);
    const resModel = plainToClassFromExist(
      new ResponsesModel<SurveyListModel>(SurveyListModel),
      resData,
    );
    if (resModel.status == 200 && typeof resModel.data == "object") {
      let common = new Array();
      let adhoc = new Array();
      let activity = new Array();

      // let data = resModel.data;

      await Promise.all(
        resModel.data.map(async (item: any) => {
          let lst = new Array();

          await Promise.all(
            item?.list?.map(async (itm: any) => {
              let row = plainToClass(SurveyItemModel, itm);
              lst.push(row);
            }),
          );

          if (item.type == "common") {
            // data.common = lst;
            await Promise.all(
              lst?.map(async (itm: SurveyItemModel) => {
                if (itm.isActivity) {
                  activity.push(itm);
                } else {
                  common.push(itm);
                }
              }),
            );
          } else {
            // data.adhoc = lst;
            await Promise.all(
              lst?.map(async (itm: SurveyItemModel) => {
                if (itm.isActivity) {
                  activity.push(itm);
                } else {
                  adhoc.push(itm);
                }
              }),
            );
          }
        }),
      );

      activity = activity?.sort((a, b) => a.id - b.id);

      let data: any = {
        common: [...common],
        adhoc: [...adhoc],
        activity: [...activity],
      };

      return { data: data, total: resModel.total ?? 0 };
    }
    return undefined;
  };

  fetchPostSurveyManagement = async (params: any = {}) => {
    const resData = await api.postSurveyManagement(params);
    const resModel = plainToClassFromExist(
      new ResponseModel<SuccessSurveyModel>(SuccessSurveyModel),
      resData,
    );
    if (resModel.status == 200 && typeof resModel.data == "object") {
      return resModel.data;
    }
    return undefined;
  };

  fetchClosedReason = async (params: any = {}) => {
    const resData = await api.getClosedReason(params);
    const resModel = plainToClassFromExist(
      new ResponseModel<ClosedReasonItemModel[]>(ClosedReasonItemModel),
      resData,
    );
    if (resModel.status == 200 && typeof resModel.data == "object") {
      return resModel.data;
    }
    return undefined;
  };

  fetchPostClosedShop = async (params: any = {}) => {
    const resData = await api.postClosedShop(params);
    const resModel = plainToClassFromExist(
      new ResponseModel<SuccessModel>(SuccessModel),
      resData,
    );
    if (resModel.status == 200 && typeof resModel.data == "object") {
      return resModel.data;
    }
    return undefined;
  };

  fetchPostNoContact = async (params: any = {}) => {
    const resData = await api.postNoContact(params);
    const resModel = plainToClassFromExist(new ResponseModel<SuccessModel>(SuccessModel), resData);
    if (resModel.status == 200 && typeof resModel.data == "object") {
      return resModel.data;
    }
    return undefined;
  };

  fetchGetIsRequireCheckout = async (params: { customerId: number }) => {
    const resData = await api.getIsRequireCheckout(params);
    const resModel = plainToClassFromExist(
      new ResponseModel<{ isCheckOutRequired: boolean }>(Object),
      resData,
    );
    if (resModel.status == 200) {
      return resModel.data;
    }
    return undefined;
  };

  fetchGetNoActivityReason = async (params: { channelId: number }) => {
    const resData = await api.getNoActivityReason(params);
    const resModel = plainToClassFromExist(
      new ResponseModel<OutRangeVisitItemModel[]>(OutRangeVisitItemModel),
      resData,
    );
    if (resModel.status == 200 && typeof resModel.data == "object") {
      return resModel.data;
    }
    return undefined;
  };

  fetchCheckActivityBeforeCheckout = async (params: {
    saleOrgCriteriaId: number;
    customerId: number;
    customerPlanId: number;
  }) => {
    const resData = await api.checkActivityBeforeCheckout(params);
    const resModel = plainToClassFromExist(
      new ResponseModel<CheckActivityBeforeCheckout>(
        CheckActivityBeforeCheckout,
      ),
      resData,
    );
    if (resModel.status == 200 && typeof resModel.data == "object") {
      return resModel.data;
    }
    return undefined;
  };
}
